import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, CalendarOff, FileText, GraduationCap, Home, LogOut, RefreshCw, Sparkles, User, Wallet, Building2 } from "lucide-react";
import { BoutonChoixEspace, CLE_ROLE, ouvrirEspace, useRoles } from "../components/ChoixEspace";
import { Ligne } from "../espace/ui";
import { profsAvecPhoto } from "../lib/photosProfs";
import { CLE, api, erreurDe } from "./commun";
import { espaceApi } from "../espace/api";
import { Cours, Appel } from "./Cours";
import { Heures } from "./Heures";
import Prives from "./Prives";
import { Paie, Absences, Profil } from "./Autres";

/* ── Connexion : avec le compte de l'app (Google, mot de passe), ou code par e-mail ── */
const Connexion = ({ onConnecte }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [etape, setEtape] = useState("email");
  const [message, setMessage] = useState("");
  const [erreur, setErreur] = useState("");
  const demander = async (e) => {
    e.preventDefault(); setErreur("");
    try { const { data } = await api.post("/code", { email }); setMessage(data.message); setEtape("code"); } catch (x) { setErreur(erreurDe(x)); }
  };
  const valider = async (e) => {
    e.preventDefault(); setErreur("");
    try { const { data } = await api.post("/session", { email, code }); localStorage.setItem(CLE, data.session); onConnecte(); } catch (x) { setErreur(erreurDe(x)); }
  };
  return (
    <div className="min-h-[100dvh] px-5 py-10 flex flex-col justify-center" style={{ background: "var(--bg-soft, #EEF6F8)" }} data-testid="prof-connexion">
      <div className="max-w-sm w-full mx-auto space-y-5">
        <div className="text-center">
          <img src="/img/app-192.png" alt="1000 ARTS" className="w-16 h-16 rounded-2xl mx-auto" />
          <h1 className="font-display text-[26px] mt-4">Espace prof</h1>
          <p className="text-sm text-[var(--muted)] mt-1">Tes cours, tes listes, tes heures.</p>
        </div>
        <button className="btn-primary w-full justify-center" data-testid="prof-connexion-app"
          onClick={() => { try { localStorage.setItem(CLE_ROLE, "prof"); } catch { /* rien */ } navigate("/espace"); }}>
          Me connecter avec mon compte de l'app
        </button>
        <p className="text-center text-[12.5px] text-[var(--muted)] -mt-2">
          Famille, prof ou direction : c'est la même connexion.
        </p>
        <div className="card-flat bg-white">
          <div className="text-[13px] font-semibold text-[var(--muted)] mb-3">Ou avec un code reçu par e-mail</div>
          {etape === "email" ? (
            <form onSubmit={demander} className="space-y-3">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Ton adresse e-mail" className="input-field w-full" data-testid="prof-email" />
              <button className="btn-ghost w-full justify-center" data-testid="prof-demander-code">Recevoir mon code <ArrowRight size={15} /></button>
            </form>
          ) : (
            <form onSubmit={valider} className="space-y-3">
              <p className="text-xs text-[var(--muted)]">{message}</p>
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="Code à 6 chiffres"
                className="input-field w-full text-center tracking-[0.4em] text-lg" data-testid="prof-code" />
              <button className="btn-primary w-full justify-center" data-testid="prof-valider-code">Entrer</button>
              <button type="button" onClick={() => setEtape("email")} className="text-xs underline w-full">Changer d'adresse</button>
            </form>
          )}
          {erreur && <p className="text-sm mt-3" style={{ color: "#B91C1C" }}>{erreur}</p>}
        </div>
      </div>
    </div>
  );
};

const ONGLETS = [
  { id: "cours", label: "Mes cours", titre: "Mes cours", Icone: CalendarDays },
  { id: "heures", label: "Heures", titre: "Mes heures", Icone: Wallet },
  { id: "paie", label: "Paie", titre: "Mes fiches de paie", Icone: FileText },
  { id: "absences", label: "Absences", titre: "Mes absences", Icone: CalendarOff },
  { id: "profil", label: "Profil", titre: "Mon profil", Icone: User },
];
// Cours privés (Lydia, Miller, Noémie ; version de test d'abord) : un onglet de plus.
const ONGLET_PRIVES = { id: "prives", label: "Privés", titre: "Cours privés", Icone: Sparkles };

/* L'espace des profs : la semaine, l'appel, les heures, la paie, ses coordonnées. */
export default function EspaceProf() {
  const navigate = useNavigate();
  const roles = useRoles();
  const [moi, setMoi] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [onglet, setOngletBrut] = useState(() => { try { return sessionStorage.getItem("prof_onglet") || "cours"; } catch { return "cours"; } });
  const [seance, setSeance] = useState(null);
  // Le mois que l'onglet Heures ouvre : celui de la carte « Tes heures d'octobre sont à
  // envoyer » (elle ouvrait novembre, le mois en cours — tests de l'école, 01.11.2026).
  const [moisHeures, setMoisHeures] = useState("");
  // Mise à jour discrète (fiche de paie validée, nouveau cours…) : une erreur réseau passagère
  // ne déconnecte pas le prof, on réessaiera au prochain passage (école, 30.10.2026 : « la
  // fiche de paie n'apparaît qu'en relançant l'app »).
  const rafraichir = useCallback(async () => {
    try {
      const { data } = await api.get("/moi");
      if (data?.prof) setMoi(data);
    } catch { /* on garde l'écran tel quel */ }
  }, []);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") rafraichir(); }, 120000);
    return () => clearInterval(t);
  }, [rafraichir]);

  const setOnglet = (o, mois = "") => {
    setOngletBrut(o); setSeance(null); setMoisHeures(mois);
    // Une fiche de paie validée entre-temps par l'école apparaît sans relancer l'app.
    rafraichir();
    try { sessionStorage.setItem("prof_onglet", o); } catch { /* rien */ }
    document.getElementById("prof-defilement")?.scrollTo({ top: 0 });
  };

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/moi");
      // Session prolongée (plus d'une semaine) : la neuve remplace l'ancienne.
      if (data.session) { try { localStorage.setItem(CLE, data.session); } catch { /* rien */ } }
      setMoi(data);
    } catch { setMoi(false); } finally { setChargement(false); }
  }, []);

  useEffect(() => {
    document.body.classList.add("espace-app");
    // L'école (ou le changement d'espace) ouvre l'espace d'un prof avec ?jeton=…
    const jeton = new URLSearchParams(window.location.search).get("jeton");
    if (jeton) { localStorage.setItem(CLE, jeton); window.history.replaceState({}, "", "/prof"); }
    charger();
    const auRetour = () => { if (document.visibilityState === "visible") charger(); };
    document.addEventListener("visibilitychange", auRetour);
    return () => { document.body.classList.remove("espace-app"); document.removeEventListener("visibilitychange", auRetour); };
  }, [charger]);

  if (chargement) return <div className="min-h-[100dvh] grid place-items-center"><RefreshCw size={22} className="animate-spin" style={{ color: "var(--primary)" }} /></div>;
  if (!moi) return <Connexion onConnecte={charger} />;

  const quitter = async () => {
    if (!window.confirm("Se déconnecter de l'application ?")) return;
    // Le cookie de session de l'app est aussi fermé : sinon on était reconnecté aussitôt.
    try { await espaceApi.post("/deconnexion"); } catch { /* déjà déconnecté */ }
    ["prof_session", "espace_session", "admin_token", CLE_ROLE].forEach((k) => { try { localStorage.removeItem(k); } catch { /* rien */ } });
    // Retour à l'écran de connexion COMMUN (famille, prof, direction) : depuis l'écran
    // « Espace prof », on ne pouvait plus se connecter avec un compte famille.
    navigate("/espace");
  };
  const photo = profsAvecPhoto(moi.prof.prenom)[0]?.image;
  const onglets = moi.cours_prives_onglet ? [...ONGLETS.slice(0, 2), ONGLET_PRIVES, ...ONGLETS.slice(2)] : ONGLETS;
  const titre = onglets.find((o) => o.id === onglet)?.titre;
  const autres = (
    <>
      {roles?.ecole && <Ligne Icone={Building2} couleur="#0F1B2D" titre="Mode école" sousTitre="La console au format téléphone" onClick={() => ouvrirEspace("ecole", navigate)} />}
      {roles?.famille && <Ligne Icone={Home} couleur="#00C6CE" titre="Mon espace famille" sousTitre="Les cours de mes enfants" onClick={() => ouvrirEspace("famille", navigate)} />}
    </>
  );
  const ecrans = {
    cours: seance ? <Appel seance={seance} onRetour={() => { setSeance(null); charger(); }} /> : <Cours moi={moi} onOuvrir={setSeance} onHeures={(m) => setOnglet("heures", m)} />,
    heures: moi.paye ? <Heures key={moisHeures} moi={moi} moisDepart={moisHeures} /> : <div className="rounded-[22px] bg-white p-5 text-sm text-[var(--muted)]" style={{ border: "1px solid var(--border)" }}>Pas de décompte d'heures pour ce compte (direction).</div>,
    paie: <Paie moi={moi} />,
    absences: <Absences moi={moi} onChange={charger} />,
    profil: <Profil moi={moi} onChange={charger} onQuitter={quitter} choixEspace={autres} />,
    prives: moi.cours_prives_onglet ? <Prives /> : null,
  };

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden" style={{ background: "var(--bg-soft, #EEF6F8)" }} data-testid="prof-app">
      <header className="sticky top-0 z-30 border-b"
        style={{ background: "rgba(255,255,255,0.86)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
          borderColor: "var(--border)", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="max-w-lg mx-auto px-5 py-3 flex items-center gap-3">
          {photo ? <img src={photo} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" style={{ border: "2px solid #fff", boxShadow: "0 0 0 1px var(--border)" }} />
            : <span className="w-11 h-11 rounded-full grid place-items-center text-white font-semibold shrink-0" style={{ background: "#9333EA" }}>{moi.prof.prenom[0]}</span>}
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold leading-tight truncate" data-testid="prof-nom">{moi.prof.nom_complet || moi.prof.prenom}</div>
            <div className="text-[11.5px] text-[var(--muted)] leading-tight flex items-center gap-1"><GraduationCap size={12} /> Prof · {titre}</div>
          </div>
          <BoutonChoixEspace actuel="prof" roles={roles} />
          <button onClick={quitter} aria-label="Se déconnecter" className="w-10 h-10 grid place-items-center rounded-full shrink-0"
            style={{ background: "var(--primary-tint)", color: "var(--muted)" }}><LogOut size={16} /></button>
        </div>
      </header>
      <main id="prof-defilement" className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="max-w-lg mx-auto px-5 py-5" style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}>
          {onglet === "cours" && !seance && <h1 className="font-display text-[28px] leading-tight mb-4">Bonjour {moi.prof.prenom} 👋</h1>}
          {ecrans[onglet] ?? ecrans.cours}
        </div>
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-40 border-t"
        style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
          borderColor: "var(--border)", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)", paddingTop: "2px" }}>
        <div className="max-w-lg mx-auto grid" style={{ gridTemplateColumns: `repeat(${onglets.length}, minmax(0, 1fr))` }}>
          {onglets.map(({ id, label, Icone }) => {
            const ouvert = onglet === id;
            return (
              <button key={id} onClick={() => setOnglet(id)} data-testid={`prof-nav-${id}`} aria-current={ouvert ? "page" : undefined}
                className="relative flex flex-col items-center justify-center gap-1.5 py-3 min-h-[64px]"
                style={{ color: ouvert ? "var(--primary)" : "#475569" }}>
                <Icone size={24} strokeWidth={ouvert ? 2.4 : 2} />
                <span className="text-[12px] font-semibold leading-none">{label}</span>
                {ouvert && <span className="absolute top-0 w-8 h-[2.5px] rounded-full" style={{ background: "var(--primary)" }} />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
